// src/constants/tableHints.js
//
// Per-table context shown in the chat composer so the user knows what the
// currently selected agent/table can actually answer.
//
// Keys are `domain` -> `table.full` from src/constants/tableLabels.js
// (which in turn matches table_name in the backend's sap_modules.json).
//
//   placeholder - short, goes inside the input as "Ask anything about ..."
//   description - one line under the input naming the data that is there
//
// A domain can also carry a `__default` entry, used when no table is selected
// yet or when a table has no entry of its own.

const tableHints = {
  'Purchase Orders': {
    __default: {
      placeholder: 'purchase orders',
      description:
        'This agent answers questions about purchase orders - headers, items, delivery schedules and pricing conditions.',
    },
    PurchaseOrder: {
      placeholder: 'PO headers',
      description:
        'PO header data: PO number, company code, PO type, supplier, purchasing org/group, order date, validity, currency, payment terms and processing/release status.',
    },
    PurchaseOrderItem: {
      placeholder: 'PO line items',
      description:
        'PO line items: material and item text, plant, storage location, material group, order quantity and unit, net price, and the info record / supplier material behind each line.',
    },
    PurchaseOrderScheduleLines: {
      placeholder: 'PO schedule lines',
      description:
        'PO delivery schedules per item: scheduled and statistical delivery dates, ordered vs committed quantity and quantity already delivered - use it for delivery and OTIF questions.',
    },
    PurchaseOrderPricing: {
      placeholder: 'PO pricing conditions',
      description:
        'PO pricing conditions per item: each pricing step (PBXX gross price, NAVS tax, SKTO discount...) with condition rate, amount and currency - use it to break down how a PO price was built.',
    },
  },

  'Purchase Requisition': {
    __default: {
      placeholder: 'purchase requisitions',
      description:
        'This agent answers questions about purchase requisitions - headers and their line items - and can create a new PR.',
    },
    PurchaseRequisitionHeader: {
      placeholder: 'PR headers',
      description:
        'PR header data: requisition number, document type (e.g. NB) and description. Use it for counts of requisitions, PR types and open requisitions.',
    },
    PurchaseRequisitionItem: {
      placeholder: 'PR line items',
      description:
        'PR line items: material and item text, material group, quantity and unit, plant, company code, purchasing org/group, delivery date, release status and the PO that followed on.',
    },
  },

  'Goods Movement': {
    __default: {
      placeholder: 'goods movements',
      description:
        'This agent answers questions about goods receipts and material documents, and can post a GR against a purchase order.',
    },
    GRN: {
      placeholder: 'goods receipts',
      description:
        'Goods receipt lines: material document and item, material, plant, storage location, batch, movement type, quantity and unit, and the referenced PO / PO item, supplier, cost centre and G/L account.',
    },
    MaterialDocumentHeader: {
      placeholder: 'GR document headers',
      description:
        'Material document headers: document number and year, document and posting date, created by, goods movement code, header text and reference document.',
    },
  },

  'Vendor Invoice': {
    __default: {
      placeholder: 'vendor invoices',
      description:
        'This agent answers questions about supplier invoices and their PO references, and can post an invoice or a direct FB60 entry.',
    },
    VendorInvoice: {
      placeholder: 'vendor invoice headers',
      description:
        'Supplier invoice headers: invoice number and fiscal year, company code, document and posting date, invoicing party, reference number, currency and gross amount.',
    },
    VendorInvoiceItem: {
      placeholder: 'invoice line items',
      description:
        'Invoice line items with their PO reference: which PO and PO item each line settles, the goods receipt it references, invoiced quantity and amount, and the tax code - use it for three-way match questions.',
    },
    direct_post: {
      placeholder: 'direct FB60 postings',
      description:
        'Direct (non-PO) accounting postings: company code, document number, fiscal year, document type, posting/document date, reference, and each line item with account type, posting key, amount, G/L account and tax code.',
    },
  },

  Vendor: {
    __default: {
      placeholder: 'vendors',
      description:
        'This agent answers questions about vendor master data and vendor status.',
    },
    VendorMaster: {
      placeholder: 'vendor master data',
      description:
        'Vendor master records: vendor number and name, street, city, postal code, region, country, language, company code, purchasing organization and order currency.',
    },
    Supplier: {
      placeholder: 'vendor status',
      description:
        'Supplier records from the business partner API: supplier ID, full name, account group, created by/date, linked customer, VAT registration and tax numbers.',
    },
  },

  'Vendor Ledger': {
    __default: {
      placeholder: 'the vendor ledger',
      description:
        'This agent answers questions about vendor open items and cleared items in the accounting ledger.',
    },
    accounting: {
      placeholder: 'vendor open items',
      description:
        'Vendor ledger line items: company code, document number, fiscal year, line item, vendor, amount, debit/credit indicator, posting and document date, payment terms and method, and clearing date/document - use it for open items and ageing.',
    },
  },

  Inventory: {
    __default: {
      placeholder: 'inventory',
      description:
        'This agent answers questions about stock, material documents and physical inventory counts.',
    },
    MaterialDocumentHeader: {
      placeholder: 'material document headers',
      description:
        'Material document headers: document number and year, transaction type, document and posting date, created by, goods movement code and reference document.',
    },
    MaterialDocumentitem: {
      placeholder: 'material document items',
      description:
        'Material document line items: material, plant, storage location, batch, movement type, stock type, quantity and unit, and links to the PO, sales order, cost centre, profit centre and G/L account.',
    },
    Material_stock_information: {
      placeholder: 'material stock',
      description:
        'Current stock by material: plant, storage location, batch, stock type and special stock, with the supplier/customer or SD document the stock is assigned to.',
    },
    PhysicalInventoryDocumentHeader: {
      placeholder: 'physical inventory documents',
      description:
        'Physical inventory document headers: document number and fiscal year, plant, storage location, planned count date, posting date, count status and adjustment posting status.',
    },
    PhysicalInventoryDocumentItem: {
      placeholder: 'physical inventory items',
      description:
        'Physical inventory count lines: material, plant, storage location, batch, counted quantity and unit, who counted it and when, and the material document posted for the adjustment.',
    },
  },

  'Sales Order': {
    __default: {
      placeholder: 'sales orders',
      description:
        'This agent answers questions about sales orders and their line items.',
    },
    SalesOrder: {
      placeholder: 'sales order headers',
      description:
        'Sales order headers: order number and type, sales organization, distribution channel, division, sold-to party, order and requested delivery date, currency, payment terms, delivery block and overall process status.',
    },
    SalesOrderItem: {
      placeholder: 'sales order items',
      description:
        'Sales order line items: item number and category, material, item text, requested quantity and unit, plant, storage location, shipping point and type, material group and pricing date.',
    },
  },

  'Business Partner': {
    __default: {
      placeholder: 'business partners',
      description:
        'This agent answers questions about business partner, customer and supplier master data.',
    },
    Customer: {
      placeholder: 'customer master data',
      description:
        'Customer master records: customer ID, name, account group, created by/date, billing/delivery/order blocks, tax numbers and VAT registration.',
    },
    'Business Partner': {
      placeholder: 'business partners',
      description:
        'Business partner master records: BP ID with its customer/supplier IDs, category and grouping, full name, organization names, search terms, language and created by/date.',
    },
    Supplier: {
      placeholder: 'supplier master data',
      description:
        'Supplier master records: supplier ID, full name, account group, linked customer, created by/date, VAT registration and tax numbers.',
    },
  },

  'Cost Center': {
    __default: {
      placeholder: 'cost centres and G/L accounts',
      description:
        'This agent answers questions about cost centre and G/L account master data.',
    },
    CostCenterMaster: {
      placeholder: 'cost centre master data',
      description:
        'Cost centre master records: controlling area, cost centre, validity dates, company code, business area, responsible person, currency, profit centre, department, category and posting blocks.',
    },
    GLAccountMaster: {
      placeholder: 'G/L account master data',
      description:
        'G/L account master records: chart of accounts, G/L account and account group, P&L vs balance sheet type, functional area, partner company and created by/date.',
    },
  },

  'Human Resource': {
    __default: {
      placeholder: 'HR',
      description:
        'This agent answers HR questions - leave balances, attendance and employee records.',
    },
    'All functions': {
      placeholder: 'HR and leave',
      description:
        'HR data from Zoho People: leave booked and balance by leave type, attendance and employee details.',
    },
  },

  'Policy Repository': {
    __default: {
      placeholder: 'company policies',
      description:
        'This agent answers questions from the uploaded policy documents.',
    },
    Documents: {
      placeholder: 'policy documents',
      description:
        'Answers are drawn from the indexed policy documents - ask about a policy, a clause or a procedure and the agent quotes the relevant document.',
    },
  },

  Analytics: {
    __default: {
      placeholder: 'financial analytics',
      description:
        'This agent analyses the company financial statements and emails back a written report.',
    },
    'Finance Reports': {
      placeholder: 'financial analysis',
      description:
        'Ask about revenue, margins, ratios, leverage and working capital - the agent reads the financial statements and emails back a written report addressed to the CFO.',
    },
  },

  'Finance Analyst': {
    __default: {
      placeholder: 'the financial statements',
      description:
        'Ask about the company financial statements and get the answer here in chat, with a chart when the numbers are worth comparing.',
    },
    'Finance Analyst': {
      placeholder: 'the financial statements',
      description:
        'Ask about revenue, profit, balance sheet and cash - answered in chat. Ask for a graph or a trend to get a chart.',
    },
  },

  Palmtop: {
    __default: {
      placeholder: 'the shipping master plan',
      description:
        'This agent answers questions about the shipping master plan database.',
    },
    master_plan: {
      placeholder: 'the shipping master plan',
      description:
        'Master plan shipping data - ask in plain English and the agent turns it into a query over the plan.',
    },
  },
};

/**
 * Returns { placeholder, description } for the selected domain/table, falling
 * back to the domain default and then to null.
 */
export function getTableHint(domain, table) {
  const forDomain = tableHints[domain];
  if (!forDomain) return null;
  return (table && forDomain[table]) || forDomain.__default || null;
}

export default tableHints;
